import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { isServerRender, noopStorage } from '../lib/ssr-safe-storage';
import type { ConsentDecision } from '@my-fullstack-app/itinerary-types';
import { useItineraryStore } from './itinerary-store';

/**
 * Per-purpose tracking-consent state — see ADR-0065.
 *
 * Single-purpose Zustand store, NOT a slice of `itinerary-store`. The
 * decision is orthogonal to trip data (lives outside the itinerary
 * domain) and the persistence key is intentionally separate from
 * `itinerary-trips` so a future store-wipe of trip data doesn't
 * accidentally clear the user's consent record.
 *
 * Anonymous users: this AsyncStorage entry IS the source of truth.
 * Authenticated users: same record, with `UserPrefsSync` mirroring it
 * to `UserPreferences.consent` on the server. Server-wins on hydrate,
 * anonymous→authenticated migration is handled in `SyncManagerInit`.
 */

/** Bumped when the privacy policy or the set of purposes materially
 *  changes — a stored decision with a lower version triggers a
 *  re-prompt. v2: the standalone `errorReports` purpose was folded into
 *  `analytics` when mobile error reporting moved onto PostHog (ADR-0065);
 *  the bump re-prompts everyone so the new scope is explicit. */
export const CURRENT_CONSENT_VERSION = 2;

const STORAGE_KEY = 'consent:v1';

export interface ConsentState {
  /** The persisted decision, or `null` when the user has never
   *  decided (or has cleared their decision). */
  consent: ConsentDecision | null;
  /** False until the AsyncStorage rehydrate completes. The consent
   *  sheet uses this to avoid flashing on first paint. */
  hasHydrated: boolean;
  /** Ephemeral UI flag — true when the user has tapped the "Privacy
   *  & Tracking" entry on `/profile` to re-open the sheet for review.
   *  Not persisted. The sheet renders for either `reviewOpen === true`
   *  or `needsPrompt() === true`. */
  reviewOpen: boolean;
  /** Session-scoped flag — true after the user dismissed the first-launch
   *  prompt (drag-down / backdrop tap) without recording a decision.
   *  Suppresses re-prompts for the rest of the session so the sheet
   *  doesn't immediately snap back open. Not persisted; cleared on the
   *  next app launch so the prompt reappears, per ADR-0065. */
  dismissedThisSession: boolean;
  /** Record a fresh decision. Stamps `decisionAt` and `version`
   *  from current values. */
  setConsent: (
    purposes: Pick<ConsentDecision, 'analytics' | 'affiliates'>
  ) => void;
  /** Drop the local decision — used by tests and the "reset all
   *  privacy choices" debug action. */
  clearConsent: () => void;
  /** Replace local state with a server-resolved decision. Called by
   *  `UserPrefsSync.applyServer` on hydrate. Bypasses the
   *  `decisionAt` / `version` stamping that `setConsent` does. */
  hydrateFromServer: (decision: ConsentDecision | null) => void;
  /** True when the consent sheet should be shown — either no decision
   *  on record, or the recorded version predates the current policy. */
  needsPrompt: () => boolean;
  /** Imperatively open the sheet for review. Called from `/profile`
   *  → "Privacy & Tracking". */
  openReview: () => void;
  /** Imperatively close a review session. The first-launch prompt
   *  closes itself on Accept / Save; review sessions close on
   *  backdrop tap / dismiss. */
  closeReview: () => void;
  /** Mark the first-launch prompt as dismissed for this session so
   *  the sheet stops re-snapping itself open after a drag-dismiss.
   *  No persistent state — the next launch re-evaluates. */
  dismissFirstPrompt: () => void;
}

export const useConsentStore = create<ConsentState>()(
  persist(
    (set, get) => ({
      consent: null,
      hasHydrated: false,
      reviewOpen: false,
      dismissedThisSession: false,
      setConsent: (purposes) => {
        set({
          consent: {
            ...purposes,
            decisionAt: new Date().toISOString(),
            version: CURRENT_CONSENT_VERSION,
          },
        });
        // Mark prefs dirty so the consent rides the next UserPrefsSync
        // flush. Anonymous users have no API — the flush short-circuits
        // and AsyncStorage (via the persist middleware) is their
        // durable copy. `hydrateFromServer` intentionally does NOT
        // dirty: that path applies the server's record to local state
        // and would otherwise ping-pong back to the server on hydrate.
        useItineraryStore.getState().markUserPrefsDirty();
      },
      clearConsent: () => set({ consent: null }),
      hydrateFromServer: (decision) => set({ consent: decision }),
      needsPrompt: () => {
        const c = get().consent;
        return c === null || c.version < CURRENT_CONSENT_VERSION;
      },
      openReview: () => set({ reviewOpen: true }),
      closeReview: () => set({ reviewOpen: false }),
      dismissFirstPrompt: () => set({ dismissedThisSession: true }),
    }),
    {
      name: STORAGE_KEY,
      storage: createJSONStorage(() => (isServerRender ? noopStorage : AsyncStorage)),
      // `hasHydrated` is runtime-only; methods strip themselves.
      partialize: (state) => ({ consent: state.consent }),
      onRehydrateStorage: () => (state) => {
        // Always flip `hasHydrated`, even on error — consumers waiting
        // on rehydrate would otherwise hang forever.
        useConsentStore.setState({ hasHydrated: true });
        if (state === undefined) {
          // eslint-disable-next-line no-console -- consent diagnostics
          console.info('[consent] rehydrate produced no state — defaulting to null');
        }
      },
    }
  )
);

/** Sign-out hook: wipe the consent decision from memory AND from
 *  AsyncStorage so the next visitor on a shared device sees the
 *  consent sheet fresh.
 *
 *  Without this wipe, user A's decision would linger in the unscoped
 *  'consent:v1' key after they sign out. User B signing in on the
 *  same device then sees A's UI state briefly; worse, if B has no
 *  server-side consent yet, the anonymous→authenticated migration
 *  arm in `UserPrefsSync.hydrate` (`if (server.consent === undefined
 *  && local !== null) markDirty`) would push A's decision UP to B's
 *  server record. That arm was designed for the anonymous→first-
 *  sign-up case (ADR-0065) and can't distinguish "anonymous → me"
 *  from "previous user A on shared device".
 *
 *  Doesn't clear ephemeral UI flags (reviewOpen, dismissedThisSession);
 *  those are session-only and reset on their own lifecycle. */
export async function clearConsentForLogout(): Promise<void> {
  useConsentStore.setState({ consent: null });
  await AsyncStorage.removeItem(STORAGE_KEY);
}

/** Test helper. Resets in-memory state to defaults; intentionally
 *  does NOT clear AsyncStorage (tests own that via the AsyncStorage
 *  mock). */
export function _resetConsentStore(): void {
  useConsentStore.setState({
    consent: null,
    hasHydrated: false,
    reviewOpen: false,
    dismissedThisSession: false,
  });
}
